import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import './themes.css';

// Active l'envoi automatique des cookies (httpOnly `access_token`) sur tous
// les appels axios — pré-requis pour l'authentification par cookie sécurisé
// côté serveur. Voir backend/auth.py::set_auth_cookie + CORS allow_credentials.
axios.defaults.withCredentials = true;

import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { TipsProvider } from './contexts/TipsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { getToken, setToken as persistToken, clearToken } from './lib/authStorage';
import { enforceUnloadGap, startSessionTimeouts, clearSessionArtifacts, isSessionFresh, startHeartbeat, clearHeartbeat } from './lib/sessionTimeout';
import { registerServiceWorker } from './lib/pwa';
// Landing + Auth restent en eager (chargés immédiatement — premier écran).
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
// Pages d'app chargées à la demande → split du bundle, ↓60% du JS initial.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Budget = lazy(() => import('./pages/Budget'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Market = lazy(() => import('./pages/Market'));
const Reports = lazy(() => import('./pages/Reports'));
const ArchivePage = lazy(() => import('./pages/Archive'));
const Settings = lazy(() => import('./pages/Settings'));
const Admin = lazy(() => import('./pages/Admin'));
const EmailChangeVerify = lazy(() => import('./pages/EmailChangeVerify'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerifyEmailPending = lazy(() => import('./pages/VerifyEmailPending'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const BlogIndex = lazy(() => import('./pages/Blog').then((m) => ({ default: m.BlogIndex })));
const BlogArticle = lazy(() => import('./pages/Blog').then((m) => ({ default: m.BlogArticle })));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
import { Toaster } from './components/ui/sonner';
import I18nDebugOverlay from './components/I18nDebugOverlay';
import GlobalLoadingBar from './components/GlobalLoadingBar';
import { SparklineBatchProvider } from './components/SparklineBatchContext';
import { useVersionCheck } from './hooks/useVersionCheck';
import RouteTracker from './components/RouteTracker';
import PreviewWarningBanner from './components/PreviewWarningBanner';
import DemoBanner from './components/DemoBanner';
import { initMetaPixel } from './utils/metaPixel';
initMetaPixel();

// Petit fallback affiché pendant le chargement asynchrone d'une page.
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F5EDE2]">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#8B6F47] border-t-transparent mb-3"></div>
      <p className="text-[#3E2723] text-sm font-semibold">Chargement…</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  // Détecte les nouvelles versions et force un reload du bundle si l'utilisateur
  // a un ancien JS en cache (notamment après chaque déploiement en production).
  useVersionCheck();

  // Au démarrage : si l'utilisateur revient après une fermeture longue
  // (browser closed > 5 min), on force la déconnexion AVANT de lire le token.
  // Couvre le cas Chrome « Continuer où vous en étiez » qui restaure
  // sessionStorage à la réouverture.
  if (typeof window !== 'undefined') {
    enforceUnloadGap();
  }
  // Token : vit uniquement en mémoire (lib/authStorage). Sur reload, la
  // variable est vidée → on tente de restaurer la session via le cookie
  // httpOnly côté serveur (voir effect `authChecking` plus bas).
  const [token, setToken] = useState(() => getToken());
  const [user, setUser] = useState(null);
  // `authChecking` = true tant qu'on n'a pas vérifié si un cookie session
  // existe côté serveur. Empêche un flash de redirection vers /auth quand
  // l'utilisateur recharge une page protégée.
  const [authChecking, setAuthChecking] = useState(true);

  const logout = useCallback(() => {
    // Best-effort : invalide le cookie httpOnly côté serveur. On n'attend
    // pas la réponse (logout doit être instantané côté UI même si offline).
    try {
      axios.post(`${API}/auth/logout`).catch(() => {});
    } catch { /* ignore */ }
    clearToken();
    clearSessionArtifacts();
    clearHeartbeat();
    setToken(null);
    setUser(null);
  }, []);

  // Surveillance session : inactivité 10 min + onglet caché 10 min.
  // Le 3ᵉ pilier (logout sur fermeture longue) est géré par `enforceUnloadGap`
  // au démarrage, plus la persistance d'un timestamp via pagehide.
  useEffect(() => {
    if (!token) return undefined;
    const stop = startSessionTimeouts(() => {
      logout();
    });
    return stop;
  }, [token, logout]);

  const fetchUser = useCallback(async () => {
    try {
      // axios.defaults.withCredentials=true → cookie envoyé automatiquement.
      // Header Bearer envoyé en parallèle si on a un token in-memory (rétro-compat).
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      logout();
    }
  }, [token, logout]);

  // === Restauration de session via le cookie httpOnly au démarrage ===
  // Sur reload, `_inMemoryToken` est vide. On interroge /auth/me avec le
  // cookie pour savoir si une session valide existe côté serveur. Si oui,
  // on pose un token-sentinel `COOKIE_SESSION` pour que les composants qui
  // testent `if (token)` continuent de fonctionner. Le backend ignorera ce
  // Bearer factice puisqu'il privilégie toujours le cookie.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Si on a déjà un token in-memory (fraîchement loggé), pas besoin de
      // restaurer — l'autre effect appellera fetchUser.
      if (token) { setAuthChecking(false); return; }
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (cancelled) return;
        // ANTI « Continuer où vous en étiez » : Chrome peut restaurer le
        // cookie httpOnly après une fermeture/réouverture du navigateur.
        // On n'honore le cookie que si un heartbeat récent atteste qu'une
        // session était bien en cours. Sinon on force le logout serveur.
        if (!isSessionFresh()) {
          try { axios.post(`${API}/auth/logout`).catch(() => {}); } catch { /* */ }
          return;  // reste déconnecté
        }
        setUser(response.data);
        persistToken('COOKIE_SESSION');
        setToken('COOKIE_SESSION');
      } catch {
        // Pas de session valide → on reste déconnecté.
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Heartbeat : écrit un timestamp toutes les 10 s tant que l'utilisateur est
  // connecté ET la page visible. Permet de détecter, au prochain démarrage,
  // si la session est vraiment vivante ou simplement restaurée par le
  // navigateur après fermeture. Voir lib/sessionTimeout.js::isSessionFresh.
  useEffect(() => {
    if (!token) return undefined;
    const stop = startHeartbeat();
    return stop;
  }, [token]);

  // Rafraîchir l'utilisateur quand on vient de se logger (token déjà en mémoire).
  useEffect(() => {
    if (token && token !== 'COOKIE_SESSION' && !user) {
      fetchUser();
    }
  }, [token, user, fetchUser]);

  // PWA : enregistre le service worker une fois au mount.
  // L'event listener `beforeinstallprompt` est posé globalement dans
  // lib/pwa.js (au moment de l'import) pour ne rater AUCUN événement
  // déclenché AVANT que ce useEffect n'ait eu le temps de tourner.
  useEffect(() => {
    registerServiceWorker();
  }, []);

  const login = useCallback((newToken) => {
    persistToken(newToken);
    setToken(newToken);
    // Login explicite → coupe court à toute vérification de session en cours
    // (sinon le PageLoader peut rester affiché si /auth/me du mount est lent).
    setAuthChecking(false);
  }, []);

  const authValue = useMemo(() => ({ token, user, login, logout, refreshUser: fetchUser }), [token, user, login, logout, fetchUser]);

  // Pendant la vérification de session (cookie httpOnly), on affiche un
  // loader plein écran pour éviter le flash de redirection vers /auth
  // quand l'utilisateur recharge une page protégée (Dashboard, etc.).
  if (authChecking) {
    return <PageLoader />;
  }

  return (
    <LanguageProvider>
      <CurrencyProvider>
        <AuthContext.Provider value={authValue}>
          <SubscriptionProvider>
            <TipsProvider>
            <ThemeProvider>
            <SparklineBatchProvider>
        <div className="App">
          <GlobalLoadingBar />
          <BrowserRouter>
            <PreviewWarningBanner />
            <DemoBanner />
            <RouteTracker />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Route publique d'activation de compte (lien depuis l'email) */}
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* Pages publiques légales / SEO */}
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={token ? <Navigate to="/dashboard" /> : <LandingPage />} />
              <Route path="/auth" element={token ? <Navigate to="/dashboard" /> : <AuthPage />} />
              <Route 
                path="/dashboard" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Dashboard />)} 
              />
              <Route 
                path="/transactions" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Transactions />)} 
              />
              <Route 
                path="/budget" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Budget />)} 
              />
              <Route 
                path="/portfolio" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Portfolio />)} 
              />
              <Route 
                path="/market" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Market />)} 
              />
              <Route 
                path="/reports" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Reports />)} 
              />
              <Route 
                path="/archive" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <ArchivePage />)} 
              />
              <Route 
                path="/settings" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Settings />)} 
              />
              <Route 
                path="/admin" 
                element={!token ? <Navigate to="/auth" /> : (user && !user.email_verified ? <VerifyEmailPending /> : <Admin />)} 
              />
              {/* Route publique de vérification d'un CHANGEMENT d'email (différente de l'activation initiale) */}
              <Route path="/email-change/verify" element={<EmailChangeVerify />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
          {/* Overlay de debug i18n — s'active via localStorage.i18n_debug=1
              ou ?i18n=debug ou Ctrl+Shift+I. Aucun impact en prod. */}
          <I18nDebugOverlay />
        </div>
            </SparklineBatchProvider>
            </ThemeProvider>
            </TipsProvider>
          </SubscriptionProvider>
        </AuthContext.Provider>
      </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;